'use client';

import ActionButton from '@/components/form/ActionButton/ActionButton';
import GfColorList from '@/components/gf/GfColorList/GfColorList';
import GfSizeTableSection from '@/components/gf/GfSizeTableSection/GfSizeTableSection';
import ProductDetailAccordion from '@/components/gf/ProductDetailAccordion/ProductDetailAccordion';
import ProductDetailHeader from '@/components/gf/ProductDetailHeader/ProductDetailHeader';
import ProductDetailInfo from '@/components/gf/ProductDetailInfo/ProductDetailInfo';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import { PRODUCT_TYPE, type ProductDetail } from '@/constants';
import { useGlobalLoadingAtom } from '@/state';
import layoutStyles from '@/styles/modules/layout.module.scss';
import utilsStyles from '@/styles/modules/utils.module.scss';
import type { UserDesignItem } from '@/types';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { buildSwatchOptions } from '../../hooks/useTemplateSelection';
import PhotoList from '../PhotoList/PhotoList';
import ProductUserDesignsSlider from '../ProductUserDesignsSlider/ProductUserDesignsSlider';
import styles from './ProductDetailContainerFromProductPage.module.scss';

type ProductFormValues = {
  productId: number;
  colorId: number;
};

function ProductDetailContainerFromProductPage({
  productDetail,
  userDesigns,
  openModalUserPrintId,
}: {
  productDetail: ProductDetail;
  userDesigns: UserDesignItem[];
  openModalUserPrintId?: string;
}) {
  const router = useRouter();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();
  const [isOpen, setIsOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedVariantCode, setSelectedVariantCode] = useState('');

  const isTemplateProduct = productDetail.productType === PRODUCT_TYPE.template;
  const templateDetail = productDetail.productType === PRODUCT_TYPE.template ? productDetail : null;
  const swatchOptions = templateDetail ? buildSwatchOptions(templateDetail) : [];

  const sliderImages =
    productDetail.productType === PRODUCT_TYPE.template ? productDetail.templateImages : productDetail.images;

  const initialColorId = !isTemplateProduct && productDetail.colors.length > 0 ? productDetail.colors[0]?.id : 0;

  const { setValue, watch } = useForm<ProductFormValues>({
    defaultValues: {
      productId: productDetail.id,
      colorId: initialColorId,
    },
  });
  const selectedColorId = watch('colorId');

  // 色の選択が必要かどうか（テンプレ商品では非表示）
  const noOptionSelectedColor = isTemplateProduct || productDetail.colors.length <= 1;
  const showSizeTable = !isTemplateProduct && productDetail.sizes.length > 0;

  const handleImageClick = (index: number) => {
    setActiveImageIndex(index);
  };

  const handleColorSelect = (colorId: number) => {
    if (isTemplateProduct) return;
    setValue('colorId', colorId);
    const firstImageIndexForColor = productDetail.images.findIndex((image) => image.gfProductColorId === colorId);
    if (firstImageIndexForColor !== -1) {
      setActiveImageIndex(firstImageIndexForColor);
    }
  };

  const handleSwatchSelect = (variantCode: string) => {
    if (!templateDetail) return;
    setSelectedVariantCode(variantCode);
    if (templateDetail.templateType === 'acrylic_stand') return;
    const template = templateDetail.printAreas[0]?.templates.find((t) => t.backgroundVariantCode === variantCode);
    if (!template) return;
    const targetIndex = templateDetail.templateImages.findIndex((image) => image.templateId === template.templateId);
    if (targetIndex !== -1) {
      setActiveImageIndex(targetIndex);
    }
  };

  const handleGoToWorks = () => {
    startGlobalLoading();
    router.push('/works?sort=created_at_desc');
  };

  const handleGoToProductList = () => {
    startGlobalLoading();
    router.push('/product');
  };

  return (
    <div className={`${layoutStyles.pageContainerLargeNoPaddingSp}`}>
      <div className={styles.productDetailContainer}>
        <ProductDetailHeader productDetail={productDetail} />
        <div className={styles.productImageColumn}>
          <PhotoList images={sliderImages} activeIndex={activeImageIndex} onImageClick={handleImageClick} />
          {!noOptionSelectedColor && (
            <div className={`${utilsStyles.onlyPc} ${styles.productSpecContainer}`}>
              <ProductDetailInfo specs={productDetail.specs} description={productDetail.description} />
            </div>
          )}
        </div>
        {/* スマホのみ詳細をアコーディオンで表示 */}
        <div className={utilsStyles.onlySp}>
          <div className={styles.productDescriptionAccordionContainer}>
            <ProductDetailAccordion
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              specs={productDetail.specs}
              productDetailDescription={productDetail.description}
            />
          </div>
        </div>
        <div className={styles.productOptionsContainer}>
          {/* オプション未選択パターンの場合はPC表示で右側に詳細を表示 */}
          {noOptionSelectedColor && (
            <div className={`${utilsStyles.onlyPc}`}>
              <div className={styles.noOptionsSelectedSpecContainer}>
                <ProductDetailInfo specs={productDetail.specs} description={productDetail.description} />
              </div>
            </div>
          )}
          {/* カラー選択（テンプレ商品） */}
          {isTemplateProduct && swatchOptions.length > 0 && (
            <div className={styles.productColorContainer}>
              <p className={styles.optionLabel}>カラー</p>
              <div className={styles.templateSwatchList}>
                {swatchOptions.map((option) => {
                  const isSelected = selectedVariantCode === option.value;
                  return (
                    <button
                      key={option.value}
                      type='button'
                      onClick={() => handleSwatchSelect(option.value)}
                      className={`${styles.templateSwatch} ${isSelected ? styles.templateSwatchSelected : ''}`}
                      aria-label={option.label}
                    >
                      <div className={styles.templateSwatchIconWrapper}>
                        <Image
                          src={option.swatchImageUrl}
                          alt={option.label}
                          fill
                          className={styles.templateSwatchIcon}
                          unoptimized
                        />
                      </div>
                      <span className={styles.templateSwatchLabel}>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* 色選択（非テンプレ商品） */}
          {!isTemplateProduct && productDetail.colors.length > 1 && (
            <div className={styles.productColorContainer}>
              <GfColorList
                colors={productDetail.colors}
                productBasePrice={productDetail.basePrice}
                onColorSelect={handleColorSelect}
                selectedColorId={selectedColorId}
                showColorName={true}
                title='色'
              />
            </div>
          )}
          {/* サイズ表 */}
          {showSizeTable && (
            <div className={styles.productSizeTableContainer}>
              <GfSizeTableSection sizeTableData={productDetail.sizeTable} />
            </div>
          )}
        </div>
        {/* みんなのデザインセクション */}
        <div className={styles.userDesignsSection}>
          {userDesigns.length > 0 && (
            <ProductUserDesignsSlider userDesignList={userDesigns} openModalUserPrintId={openModalUserPrintId} />
          )}
          {/* ボタンコンテナ */}
          <div className={styles.buttonsContainer}>
            <ActionButton onClick={handleGoToWorks} size='large'>
              作品一覧へ
              <div className={styles.actionButtonIcon}>
                <RoundArrowIcon direction='right' color='primary' />
              </div>
            </ActionButton>
            <ActionButton onClick={handleGoToProductList}>
              <div className={layoutStyles.actionButtonWrapperSmall}>
                <RoundArrowIcon direction='left' color='black' />
                <span className={styles.backButtonText}>アイテム一覧にもどる</span>
              </div>
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailContainerFromProductPage;
